// src/utils/permissions.js

/**
 * Check if a user can manage sections for a course
 * @param {Object} user - The user object with role and id
 * @param {Object} course - The course object with teacherId
 * @returns {boolean} - Whether the user can manage sections
 */
export const canManageSections = (user, course) => {
  if (!user) return false;
  
  // Admins can always manage sections
  if (['ADMIN', 'SUPER_ADMIN'].includes(user.role)) {
    return true;
  }
  
  // Teachers can manage sections for their own courses
  if (user.role === 'TEACHER' && course?.teacherId === user.id) {
    return true;
  }
  
  return false;
};

/**
 * Check if a user can manage sections for a course (backend version)
 * @param {Object} userData - The user data object with role and id
 * @param {Object} course - The course object with teacherId
 * @returns {boolean} - Whether the user can manage sections
 */
export const canManageSectionsBackend = (userData, course) => {
  if (!userData) return false;
  
  // Admins can always manage sections
  if (['ADMIN', 'SUPER_ADMIN'].includes(userData.role)) {
    return true;
  }
  
  // Teachers can manage sections for their own courses
  if (userData.role === 'TEACHER' && course?.teacherId === userData.id) {
    return true;
  }
  
  return false;
};
/**
 * Check if a user can message another user based on educational relationships
 * @param {Object} fromUser - The user trying to send a message (with role and id)
 * @param {Object} toUser - The target user (with role and id) 
 * @returns {boolean} - Whether the user can message the target
 */
export const canMessageUser = (fromUser, toUser) => {
  if (!fromUser || !toUser) return false;
  
  // Users cannot message themselves
  if (fromUser.id === toUser.id) return false;
  
  // Admins can message anyone
  if (['ADMIN', 'SUPER_ADMIN'].includes(fromUser.role)) return true;
  
  // Anyone can message admins
  if (['ADMIN', 'SUPER_ADMIN'].includes(toUser.role)) return true;
  
  // Teachers and Parents can message each other based on enrollment
  if (fromUser.role === 'TEACHER' && toUser.role === 'USER') return true;
  if (fromUser.role === 'USER' && toUser.role === 'TEACHER') return true;
  
  // Block all other combinations (teacher-to-teacher, parent-to-parent)
  return false;
};

/**
 * Get detailed permission check with database validation for educational relationships
 * @param {string} fromUserId - ID of user trying to send message
 * @param {string} toUserId - ID of target user
 * @param {Object} prisma - Prisma client instance
 * @returns {Promise<{allowed: boolean, reason?: string, relationship?: string}>}
 */
export const canMessageUserWithValidation = async (fromUserId, toUserId, prisma) => {
  try {
    if (!fromUserId || !toUserId) {
      return { allowed: false, reason: 'Invalid user IDs provided' };
    }
    
    // Users cannot message themselves
    if (fromUserId === toUserId) {
      return { allowed: false, reason: 'You cannot message yourself' };
    }
    
    // Fetch both users
    const [fromUser, toUser] = await Promise.all([
      prisma.user.findUnique({ where: { id: fromUserId }, select: { role: true, name: true } }),
      prisma.user.findUnique({ where: { id: toUserId }, select: { role: true, name: true } })
    ]);
    
    if (!fromUser || !toUser) {
      return { allowed: false, reason: 'One or both users not found' };
    }
    
    // Admins can message anyone
    if (['ADMIN', 'SUPER_ADMIN'].includes(fromUser.role)) {
      return { 
        allowed: true, 
        relationship: 'You are an administrator and can message any user' 
      };
    }
    
    // Anyone can message admins
    if (['ADMIN', 'SUPER_ADMIN'].includes(toUser.role)) {
      return { 
        allowed: true, 
        relationship: `You can message ${toUser.name} who is an administrator` 
      };
    }
    
    // Teacher to Parent - check if parent has learners in teacher's courses
    if (fromUser.role === 'TEACHER' && toUser.role === 'USER') {
      const connectionExists = await prisma.user.count({
        where: {
          id: toUserId,
          learners: {
            some: {
              enrolledSections: {
                some: {
                  course: { teacherId: fromUserId }
                }
              }
            }
          }
        }
      });
      
      if (connectionExists > 0) {
        return { 
          allowed: true, 
          relationship: `You can message ${toUser.name} as they have learners enrolled in your courses` 
        };
      } else {
        return { 
          allowed: false, 
          reason: 'You can only message parents who have learners enrolled in your courses' 
        };
      }
    }
    
    // Parent to Teacher - check if parent's learners are enrolled with teacher
    if (fromUser.role === 'USER' && toUser.role === 'TEACHER') {
      const connectionExists = await prisma.user.count({
        where: {
          id: fromUserId,
          learners: {
            some: {
              enrolledSections: {
                some: {
                  course: { teacherId: toUserId }
                }
              }
            }
          }
        }
      });
      
      if (connectionExists > 0) {
        return { 
          allowed: true, 
          relationship: `You can message ${toUser.name} as they teach your enrolled learners` 
        };
      } else {
        return { 
          allowed: false, 
          reason: 'You can only message teachers who teach your children' 
        };
      }
    }
    
    // All other combinations are blocked
    if (fromUser.role === 'TEACHER' && toUser.role === 'TEACHER') {
      return { 
        allowed: false, 
        reason: 'Teacher-to-teacher messaging is restricted for educational safety. Please contact an administrator if you need to communicate with this teacher' 
      };
    }
    
    if (fromUser.role === 'USER' && toUser.role === 'USER') {
      return { 
        allowed: false, 
        reason: 'Parent-to-parent messaging is not permitted. Please contact your child\'s teacher or an administrator' 
      };
    }
    
    return { 
      allowed: false, 
      reason: 'Messaging is not permitted between these user types' 
    };
    
  } catch (error) {
    console.error('Error checking message permission:', error);
    return { 
      allowed: false, 
      reason: 'Error validating messaging permissions. Please try again or contact support' 
    };
  }
};
